import React, { useMemo, useState } from "react";
import { AlertTriangle, FileDown, Info } from "lucide-react";
import TopBar from "../components/layout/TopBar";
import KpiRow from "../components/kpi/KpiRow";
import SectionCard from "../components/sections/SectionCard";
import UnifiedChart from "../components/charts/UnifiedChart";
import ClientDistributionChart from "../components/charts/ClientDistributionChart";
import OverdueInvoicesCard from "../components/dashboard/OverdueInvoicesCard";
import UpcomingDeadlines from "../components/dashboard/UpcomingDeadlines";
import SetupChecklist from "../components/onboarding/SetupChecklist";
import SogliaPlanningCard from "../components/dashboard/SogliaPlanningCard";
import CostiSection from "../components/dashboard/CostiSection";
import AskOlliButton from "../components/dashboard/AskOlliButton";
import FeedbackTriggerButton from "../components/feedback/FeedbackTriggerButton";
import UpgradePrompt from "../components/common/UpgradePrompt";
import InstallAppBanner from "../components/common/InstallAppBanner";
import { useAppState } from "../hooks/useAppState";
import { useSubscription } from "../hooks/useSubscription";
import { FORFETTARIO_THRESHOLD } from "../constants/ateco";
import { getAliquotaINPS } from "../constants/aliquote";
import {
  calcolaRiepilogoFiscale,
  getSogliePerAnno,
  calcolaAnniAttivita,
  getAliquotaSostitutiva,
} from "../utils/fiscalEngine";
import { exportFiscalReport } from "../services/exportService";

const currentYear = new Date().getFullYear();
const currentMonth = new Date().getMonth() + 1; // 1-12

// Formattazione italiana robusta: 1.234,56 €
function formatCurrency(value) {
  const num = Number(value || 0);
  const abs = Math.abs(num);
  const sign = num < 0 ? "-" : "";
  const [intPart, decPart] = abs.toFixed(2).split(".");
  const groupedInt = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ".");
  return `${sign}${groupedInt},${decPart} €`;
}

// Calcolo riepilogo fiscale/KPI per un dato anno. Estratto come funzione pura
// (invece di essere inline nello useMemo) così può essere richiamato sia per
// `selectedYear` (filtro dashboard) sia per `currentYear` (componenti che devono
// restare sempre sull'anno corrente, es. scadenze fiscali di giugno/novembre).
function buildFiscalCalc(year, { invoices, fiscalSettings, getFiscalDataForYear }) {
  // Anni di attività e aliquota dell'anno GUARDATO (non di oggi): il 2025 resta
  // al 5% anche aprendolo nel 2030.
  const anniAttivita = calcolaAnniAttivita(fiscalSettings.pivaYearStart, year);
  const isPrimoAnno = anniAttivita === 1;
  const aliquotaAutomatica = getAliquotaSostitutiva(fiscalSettings.pivaYearStart, year);
  const mesiTrascorsi = year === currentYear ? currentMonth : 12;
  const getMonth = (dateStr) => dateStr ? new Date(dateStr).getMonth() + 1 : 0;
  const getYear = (dateStr) => dateStr ? new Date(dateStr).getFullYear() : 0;

  const revenueYTD = invoices
    .filter((inv) => inv.issueDate && getYear(inv.issueDate) === year)
    .reduce((sum, inv) => sum + (Number(inv.grossAmount) || 0), 0);

  const revenueYTDPreviousYear = invoices
    .filter((inv) => {
      if (!inv.issueDate) return false;
      const issueYear = getYear(inv.issueDate);
      const issueMonth = getMonth(inv.issueDate);
      return issueYear === year - 1 && issueMonth <= currentMonth;
    })
    .reduce((sum, inv) => sum + (Number(inv.grossAmount) || 0), 0);

  const revenueDeltaAbs = revenueYTD - revenueYTDPreviousYear;
  const revenueDeltaPct = revenueYTDPreviousYear > 0
    ? ((revenueYTD - revenueYTDPreviousYear) / revenueYTDPreviousYear) * 100
    : 0;

  const incassiAnno = invoices
    .filter((inv) => {
      if (!inv.paid) return false;
      const dateToUse = inv.paymentDate || inv.issueDate;
      return dateToUse && getYear(dateToUse) === year;
    })
    .reduce((sum, inv) => sum + (Number(inv.grossAmount) || 0), 0);

  // Incassi YTD anno precedente (stesso periodo, per confronto trend)
  const incassiYTDPreviousYear = invoices
    .filter((inv) => {
      if (!inv.paid) return false;
      const dateToUse = inv.paymentDate || inv.issueDate;
      if (!dateToUse) return false;
      const payYear = getYear(dateToUse);
      const payMonth = getMonth(dateToUse);
      return payYear === year - 1 && payMonth <= currentMonth;
    })
    .reduce((sum, inv) => sum + (Number(inv.grossAmount) || 0), 0);

  const incassiDeltaAbs = incassiAnno - incassiYTDPreviousYear;
  const incassiDeltaPct = incassiYTDPreviousYear > 0
    ? ((incassiAnno - incassiYTDPreviousYear) / incassiYTDPreviousYear) * 100
    : 0;

  const incassiPerMeseDaFattureAnnoCorrente = {};
  for (let m = 1; m <= 12; m++) incassiPerMeseDaFattureAnnoCorrente[m] = 0;

  invoices.forEach((inv) => {
    if (!inv.paid) return;
    const issueYear = getYear(inv.issueDate);
    const payDate = inv.paymentDate || inv.issueDate;
    const payYear = getYear(payDate);
    const payMonth = getMonth(payDate);
    if (issueYear === year && payYear === year) {
      incassiPerMeseDaFattureAnnoCorrente[payMonth] += Number(inv.grossAmount) || 0;
    }
  });

  let ultimoMeseAttivo = null;
  for (let m = 12; m >= 1; m--) {
    if (incassiPerMeseDaFattureAnnoCorrente[m] > 0) {
      ultimoMeseAttivo = m;
      break;
    }
  }

  let proiezioneIncassi = null;
  let incassiUltimoMeseAttivo = 0;

  if (year !== currentYear) {
    proiezioneIncassi = incassiAnno;
  } else if (ultimoMeseAttivo !== null) {
    incassiUltimoMeseAttivo = incassiPerMeseDaFattureAnnoCorrente[ultimoMeseAttivo];
    const mesiRestantiDopoUltimoMese = 12 - ultimoMeseAttivo;
    proiezioneIncassi = incassiAnno + (incassiUltimoMeseAttivo * mesiRestantiDopoUltimoMese);
  }

  const inpsRate = getAliquotaINPS(fiscalSettings.inpsType, year);
  const riepilogo = calcolaRiepilogoFiscale({
    incassi: incassiAnno,
    coefficiente: fiscalSettings.profitabilityCoefficient,
    aliquotaInps: inpsRate / 100,
    aliquotaImposta: aliquotaAutomatica / 100,
    fiscalData: getFiscalDataForYear(year),
  });
  const totaleTasse = riepilogo.totaleDovuto;
  const nettoDisponibile = riepilogo.nettoDisponibile;

  // Soglie ragguagliate ai giorni di attività (primo anno); piene negli anni successivi.
  const { soglia, sogliaExit, giorniAttivita, isRagguagliata } = getSogliePerAnno(fiscalSettings, year);
  const percentualeSoglia = soglia > 0 ? (incassiAnno / soglia) * 100 : 0;
  const margineSoglia = soglia - incassiAnno;
  // Warning proporzionali alla soglia effettiva (su soglia piena restano 70k/80k).
  const isNearThreshold = incassiAnno >= soglia * (70 / 85);
  const isDangerThreshold = incassiAnno >= soglia * (80 / 85);
  const isOverThreshold = incassiAnno > soglia;

  const nomiMesi = ["", "Gennaio", "Febbraio", "Marzo", "Aprile", "Maggio", "Giugno",
                   "Luglio", "Agosto", "Settembre", "Ottobre", "Novembre", "Dicembre"];

  let meseRaggiungimentoSoglia = null;
  let raggiungeraSoglia = false;

  if (year === currentYear && !isOverThreshold) {
    if (ultimoMeseAttivo !== null && incassiUltimoMeseAttivo > 0) {
      if (proiezioneIncassi !== null && proiezioneIncassi >= soglia) {
        raggiungeraSoglia = true;
        const incassiMancanti = soglia - incassiAnno;
        if (incassiMancanti > 0) {
          const mesiNecessari = Math.ceil(incassiMancanti / incassiUltimoMeseAttivo);
          const meseStimato = ultimoMeseAttivo + mesiNecessari;
          if (meseStimato <= 12) {
            meseRaggiungimentoSoglia = nomiMesi[meseStimato];
          }
        }
      }
    }
  }

  return {
    anniAttivita, isPrimoAnno, aliquotaAutomatica, mesiTrascorsi,
    revenueYTD, revenueDeltaAbs, revenueDeltaPct, incassiAnno, proiezioneIncassi,
    incassiDeltaAbs, incassiDeltaPct,
    ultimoMeseAttivo, incassiUltimoMeseAttivo, totaleTasse,
    nettoDisponibile, percentualeSoglia, margineSoglia,
    isNearThreshold, isDangerThreshold, isOverThreshold,
    raggiungeraSoglia, meseRaggiungimentoSoglia,
    soglia, sogliaExit, giorniAttivita, isRagguagliata,
    inpsDovuto: riepilogo.inpsDovuto,
    inpsDaPagare: riepilogo.inpsDaPagare,
    tasseLorde: riepilogo.tasseLorde,
    tasseDaPagare: riepilogo.tasseDaPagare,
    totaleDovuto: riepilogo.totaleDovuto,
    versatoTotale: riepilogo.versatoTotale,
    residuoDaPagare: riepilogo.residuo,
    accantonamento: riepilogo.accantonamento,
  };
}

export default function DashboardPage() {
  const {
    user,
    darkMode,
    toggleDarkMode,
    selectedYear,
    setSelectedYear,
    invoices,
    fiscalSettings,
    getFiscalDataForYear,
    appSettings,
    updateAppSettings,
    isSynced,
  } = useAppState();

  const { loading: subLoading, canAccessFeature, isPro } = useSubscription();
  const dashboardLocked = !subLoading && !canAccessFeature('dashboard');
  const [showExportUpgrade, setShowExportUpgrade] = useState(false);

  const notificationsCount = 0;

  // Opzioni anno per il filtro dashboard: anni presenti nelle fatture + un range ragionevole
  // intorno all'anno corrente, così il dropdown resta utile anche senza dati storici.
  const yearOptions = useMemo(() => {
    const years = new Set();
    for (let y = currentYear - 3; y <= currentYear + 1; y++) years.add(y);
    invoices.forEach((inv) => {
      if (inv.issueDate) years.add(new Date(inv.issueDate).getFullYear());
    });
    years.add(selectedYear);
    return Array.from(years).sort((a, b) => b - a);
  }, [invoices, selectedYear]);

  const fiscalCalc = useMemo(
    () => buildFiscalCalc(selectedYear, { invoices, fiscalSettings, getFiscalDataForYear }),
    [invoices, selectedYear, fiscalSettings, getFiscalDataForYear]
  );

  // Riepilogo fiscale sempre riferito all'anno corrente (indipendente dal filtro
  // dashboard `selectedYear`): serve alle "Prossime scadenze" per calcolare gli
  // acconti di giugno/novembre, che riguardano sempre l'anno in corso.
  const fiscalCalcCurrentYear = useMemo(
    () => (selectedYear === currentYear
      ? fiscalCalc
      : buildFiscalCalc(currentYear, { invoices, fiscalSettings, getFiscalDataForYear })),
    [selectedYear, fiscalCalc, invoices, fiscalSettings, getFiscalDataForYear]
  );

  const kpis = useMemo(() => ({
    revenueYTD: fiscalCalc.revenueYTD,
    incassiAnno: fiscalCalc.incassiAnno,
    revenueDeltaAbs: fiscalCalc.revenueDeltaAbs,
    revenueDeltaPct: fiscalCalc.revenueDeltaPct,
    incassiDeltaAbs: fiscalCalc.incassiDeltaAbs,
    incassiDeltaPct: fiscalCalc.incassiDeltaPct,
    netAnnual: fiscalCalc.nettoDisponibile,
    netMonthly: fiscalCalc.mesiTrascorsi > 0 ? fiscalCalc.nettoDisponibile / fiscalCalc.mesiTrascorsi : 0,
    setAsideAnnual: fiscalCalc.totaleTasse,
    setAsideMonthly: fiscalCalc.mesiTrascorsi > 0 ? fiscalCalc.totaleTasse / fiscalCalc.mesiTrascorsi : 0,
    // Versato/residuo: già calcolati dal motore, servono al KPI "Tasse da
    // accantonare" per mostrare il residuo invece del dovuto lordo.
    versatoTotale: fiscalCalc.versatoTotale,
    residuoAnnual: fiscalCalc.residuoDaPagare,
    residuoMonthly: fiscalCalc.mesiTrascorsi > 0 ? fiscalCalc.residuoDaPagare / fiscalCalc.mesiTrascorsi : 0,
    mesiTrascorsi: fiscalCalc.mesiTrascorsi,
    proiezioneIncassi: fiscalCalc.proiezioneIncassi,
    ultimoMeseAttivo: fiscalCalc.ultimoMeseAttivo,
    incassiUltimoMeseAttivo: fiscalCalc.incassiUltimoMeseAttivo,
    margineSoglia: fiscalCalc.margineSoglia,
    percentualeSoglia: fiscalCalc.percentualeSoglia,
    isNearThreshold: fiscalCalc.isNearThreshold,
    isDangerThreshold: fiscalCalc.isDangerThreshold,
    isOverThreshold: fiscalCalc.isOverThreshold,
    raggiungeraSoglia: fiscalCalc.raggiungeraSoglia,
    meseRaggiungimentoSoglia: fiscalCalc.meseRaggiungimentoSoglia,
    soglia: fiscalCalc.soglia,
    sogliaExit: fiscalCalc.sogliaExit,
    giorniAttivita: fiscalCalc.giorniAttivita,
    isRagguagliata: fiscalCalc.isRagguagliata,
    selectedYear,
  }), [fiscalCalc, selectedYear]);

  const handleExportFiscalReport = () => {
    if (!isPro) {
      setShowExportUpgrade(true);
      return;
    }
    exportFiscalReport(selectedYear, {
      revenueYTD: fiscalCalc.revenueYTD,
      incassiAnno: fiscalCalc.incassiAnno,
      proiezioneIncassi: fiscalCalc.proiezioneIncassi,
      coefficienteRedditivita: fiscalSettings.profitabilityCoefficient,
      aliquotaAutomatica: fiscalCalc.aliquotaAutomatica,
      inpsDovuto: fiscalCalc.inpsDovuto,
      inpsDaPagare: fiscalCalc.inpsDaPagare,
      tasseLorde: fiscalCalc.tasseLorde,
      tasseDaPagare: fiscalCalc.tasseDaPagare,
      totaleDovuto: fiscalCalc.totaleDovuto,
      versatoTotale: fiscalCalc.versatoTotale,
      residuoDaPagare: fiscalCalc.residuoDaPagare,
      accantonamento: fiscalCalc.accantonamento,
      nettoDisponibile: fiscalCalc.nettoDisponibile,
    });
  };

  return (
    <div className={`min-h-screen transition-colors ${darkMode ? 'bg-neutral-950 text-white' : 'bg-neutral-50 text-neutral-900'}`}>
      <TopBar
        user={user}
        notificationsCount={notificationsCount}
        darkMode={darkMode}
        toggleDarkMode={toggleDarkMode}
        onUploadLogo={() => {}}
        isSynced={isSynced}
      />

      <main className="max-w-6xl mx-auto px-4 sm:px-6 pb-12">
        {/* Banner installazione app (PWA) */}
        <div className="mt-6">
          <InstallAppBanner darkMode={darkMode} />
        </div>

        {/* Filtro anno dashboard */}
        <div className="mt-6 flex items-center gap-2">
          <label
            htmlFor="dashboard-year-select"
            className={`text-sm font-medium ${darkMode ? 'text-neutral-300' : 'text-neutral-700'}`}
          >
            Anno
          </label>
          <select
            id="dashboard-year-select"
            value={selectedYear}
            onChange={(e) => setSelectedYear(Number(e.target.value))}
            className={`px-3 py-2 rounded-lg border text-sm font-medium focus:outline-none focus:ring-2 focus:ring-forfy-500 transition-colors ${
              darkMode
                ? 'bg-neutral-900 border-neutral-700 text-white'
                : 'bg-white border-neutral-200 text-neutral-900'
            }`}
          >
            {yearOptions.map((y) => (
              <option key={y} value={y}>{y}</option>
            ))}
          </select>
        </div>

        {/* Setup Checklist - Onboarding */}
        <div className="mt-6">
          <SetupChecklist
            darkMode={darkMode}
            invoices={invoices}
            appSettings={appSettings}
            updateAppSettings={updateAppSettings}
          />
        </div>

        {/* Azioni pagina - Export report fiscale */}
        {!dashboardLocked && (
          <div className="mt-6 flex justify-end">
            <button
              onClick={handleExportFiscalReport}
              className={`flex items-center gap-1.5 px-3 py-2 rounded-lg border text-sm font-medium transition-colors ${
                darkMode
                  ? 'border-neutral-700 bg-neutral-900 text-neutral-200 hover:bg-neutral-800'
                  : 'border-neutral-200 bg-white text-neutral-700 hover:bg-neutral-50'
              }`}
            >
              <FileDown className="w-4 h-4" />
              Esporta Report Fiscale
            </button>
          </div>
        )}

        {/* RIGA 1 & 2 - KPI Principali + Proiezioni (riepilogo fiscale) */}
        <div className="mt-6 relative">
          <div className={dashboardLocked ? "blur-sm pointer-events-none select-none" : ""}>
            <KpiRow kpis={kpis} currency="EUR" darkMode={darkMode} />
          </div>
          {dashboardLocked && (
            <div className="absolute inset-0 flex items-center justify-center">
              <UpgradePrompt
                title="Dashboard fiscale disponibile con Pro"
                message="Sblocca imponibile, tasse stimate e netto disponibile con il piano Pro."
              />
            </div>
          )}
        </div>

        {/* Upgrade prompt per export (utente non Pro che prova a esportare) */}
        {showExportUpgrade && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={() => setShowExportUpgrade(false)}>
            <div onClick={(e) => e.stopPropagation()}>
              <UpgradePrompt
                title="Export PDF disponibile con Pro"
                message="Esporta il report fiscale in PDF passando al piano Pro."
              />
            </div>
          </div>
        )}

        {/* Nota ragguaglio primo anno (se la soglia non è ancora superata: in quel
            caso il dettaglio è già dentro l'alert rosso) */}
        {fiscalCalc.isRagguagliata && !fiscalCalc.isOverThreshold && (
          <div className={`mt-4 flex items-start gap-3 p-4 rounded-xl border ${darkMode ? 'bg-neutral-900 border-neutral-800' : 'bg-white border-neutral-200'}`}>
            <Info className={`w-5 h-5 mt-0.5 shrink-0 ${darkMode ? 'text-forfy-300' : 'text-forfy-600'}`} />
            <div className={`text-sm ${darkMode ? 'text-neutral-300' : 'text-neutral-700'}`}>
              <p>
                Primo anno di attività: la soglia è ragguagliata ai {fiscalCalc.giorniAttivita} giorni
                effettivi → <strong>{formatCurrency(fiscalCalc.soglia)}</strong> (invece di {formatCurrency(FORFETTARIO_THRESHOLD)}).
              </p>
            </div>
          </div>
        )}

        {/* Alert soglia 85k */}
        {fiscalCalc.isOverThreshold && (
          <div className={`mt-4 flex items-start gap-3 p-4 rounded-xl border ${darkMode ? 'bg-red-900/30 border-red-700' : 'bg-red-50 border-red-200'}`}>
            <AlertTriangle className={`w-5 h-5 mt-0.5 shrink-0 ${darkMode ? 'text-red-400' : 'text-red-600'}`} />
            <div className={`text-sm ${darkMode ? 'text-red-200' : 'text-red-800'}`}>
              <p className="font-semibold">Superamento soglia forfettario!</p>
              <p className="mt-1">
                Gli incassi ({formatCurrency(fiscalCalc.incassiAnno)}) superano la soglia
                di {formatCurrency(fiscalCalc.soglia)}. Dall'anno prossimo potresti uscire dal regime forfettario.
              </p>
              {fiscalCalc.isRagguagliata && (
                <p className="mt-1">
                  Nel primo anno la soglia è ragguagliata ai {fiscalCalc.giorniAttivita} giorni
                  di attività: <strong>{formatCurrency(fiscalCalc.soglia)}</strong> invece di {formatCurrency(FORFETTARIO_THRESHOLD)}.
                </p>
              )}
              <div className="mt-2">
                <AskOlliButton darkMode={darkMode} label="Chiedi a Olli cosa fare" />
              </div>
            </div>
          </div>
        )}
        {!fiscalCalc.isOverThreshold && fiscalCalc.isDangerThreshold && (
          <div className={`mt-4 flex items-start gap-3 p-4 rounded-xl border ${darkMode ? 'bg-red-900/30 border-red-700' : 'bg-red-50 border-red-200'}`}>
            <AlertTriangle className={`w-5 h-5 mt-0.5 shrink-0 ${darkMode ? 'text-red-400' : 'text-red-600'}`} />
            <div className={`text-sm ${darkMode ? 'text-red-200' : 'text-red-800'}`}>
              <p className="font-semibold">Attenzione: soglia quasi raggiunta!</p>
              <p className="mt-1">
                Hai raggiunto il {Math.round(fiscalCalc.percentualeSoglia)}% della soglia.
                Margine: <strong>{formatCurrency(fiscalCalc.margineSoglia)}</strong>
                {fiscalCalc.meseRaggiungimentoSoglia && <span> — Stima: <strong>{fiscalCalc.meseRaggiungimentoSoglia}</strong></span>}
              </p>
              <div className="mt-2">
                <AskOlliButton darkMode={darkMode} label="Chiedi a Olli cosa fare" />
              </div>
            </div>
          </div>
        )}
        {!fiscalCalc.isOverThreshold && !fiscalCalc.isDangerThreshold && fiscalCalc.isNearThreshold && (
          <div className={`mt-4 flex items-start gap-3 p-4 rounded-xl border ${darkMode ? 'bg-amber-900/30 border-amber-700' : 'bg-amber-50 border-amber-200'}`}>
            <AlertTriangle className={`w-5 h-5 mt-0.5 shrink-0 ${darkMode ? 'text-amber-400' : 'text-amber-600'}`} />
            <div className={`text-sm ${darkMode ? 'text-amber-200' : 'text-amber-800'}`}>
              <p className="font-semibold">Attenzione alla soglia</p>
              <p className="mt-1">
                Hai raggiunto il {Math.round(fiscalCalc.percentualeSoglia)}% della soglia.
                Margine: <strong>{formatCurrency(fiscalCalc.margineSoglia)}</strong>
              </p>
            </div>
          </div>
        )}

        {/* Pianificazione incassi vs soglia forfettario (si auto-nasconde se non rilevante) */}
        <SogliaPlanningCard
          invoices={invoices}
          incassiAnno={fiscalCalc.incassiAnno}
          selectedYear={selectedYear}
          soglia={fiscalCalc.soglia}
          sogliaExit={fiscalCalc.sogliaExit}
          darkMode={darkMode}
        />

        {/* Costi ribaltabili (sostenuti vs ribaltati) - segue il filtro anno */}
        <CostiSection darkMode={darkMode} selectedYear={selectedYear} />

        {/* RIGA 3 - Alert Fatture da Incassare (sempre anno corrente, non segue il filtro dashboard) */}
        <div className="mt-6">
          <OverdueInvoicesCard
            invoices={invoices}
            selectedYear={currentYear}
            darkMode={darkMode}
          />
        </div>

        {/* RIGA 4 - Grafici side-by-side su desktop, impilati su mobile */}
        <div className="mt-6 grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Andamento mensile - sempre anno corrente, non segue il filtro dashboard */}
          <SectionCard
            title="Andamento mensile"
            subtitle="Confronto anno corrente vs precedenti"
            darkMode={darkMode}
          >
            <UnifiedChart invoices={invoices} selectedYear={currentYear} soglia={fiscalCalcCurrentYear.soglia} darkMode={darkMode} />
          </SectionCard>

          {/* Top Clienti - segue il filtro anno */}
          <SectionCard
            title="Top Clienti"
            subtitle={`Distribuzione fatturato ${selectedYear}`}
            darkMode={darkMode}
          >
            <ClientDistributionChart invoices={invoices} selectedYear={selectedYear} darkMode={darkMode} />
          </SectionCard>
        </div>

        {/* RIGA 5 - Prossime Scadenze (sempre anno corrente, non segue il filtro dashboard) */}
        <div className="mt-6">
          <UpcomingDeadlines
            invoices={invoices}
            selectedYear={currentYear}
            fiscalCalc={fiscalCalcCurrentYear}
            darkMode={darkMode}
          />
        </div>

        {/* Footer - Feedback discreto */}
        <div className="mt-8 flex justify-center">
          <FeedbackTriggerButton darkMode={darkMode} />
        </div>
      </main>
    </div>
  );
}
